import { useEffect, useState, useCallback, Fragment } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ListChecks, RefreshCw, Copy, ChevronDown, ChevronRight } from "lucide-react";

const statusBadge = (status) => {
  if (status === "cancelled") return "text-zinc-400 border-zinc-300";
  if (status === "delivered") return "text-neon border-neon/30";
  return "text-amber-600 border-amber-300";
};

export function AdminIspAssignments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/isp-assignments"); setRows(data); }
    catch (e) { logError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const copy = (proxies) => { navigator.clipboard.writeText(proxies.join("\n")); toast.success(`Copied ${proxies.length} ISPs`); };

  const renderRows = () => {
    if (loading) return <tr><td colSpan={5} className="p-8 text-center text-zinc-400">Loading…</td></tr>;
    if (rows.length === 0) return <tr><td colSpan={5} className="p-8 text-center text-zinc-400">No ISP assignments yet.</td></tr>;
    return rows.map((r) => (
      <Fragment key={r.order_id}>
        <tr className="border-b border-[#F2F8F7]" data-testid={`isp-assignment-row-${r.order_id}`}>
          <td className="p-4">
            <div className="font-mono text-neon">{r.username || "—"}</div>
            <div className="text-xs text-zinc-400">{r.email || "—"}</div>
          </td>
          <td className="p-4 font-mono text-ink">{r.quantity} IPs</td>
          <td className="p-4"><span className={`text-xs font-mono border rounded-full px-2 py-0.5 ${statusBadge(r.status)}`}>{r.status}</span></td>
          <td className="p-4 font-mono text-xs text-zinc-500 max-w-[180px] truncate" title={r.subscription_id || ""}>{r.subscription_id || "—"}</td>
          <td className="p-4">
            <button onClick={() => toggle(r.order_id)} className="text-sm text-zinc-500 hover:text-neon inline-flex items-center gap-1" data-testid={`isp-assignment-toggle-${r.order_id}`}>
              {open[r.order_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} {open[r.order_id] ? "Hide" : "View"} IPs
            </button>
          </td>
        </tr>
        {open[r.order_id] && (
          <tr key={`${r.order_id}-detail`} className="border-b border-[#F2F8F7] bg-base">
            <td colSpan={5} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono text-zinc-400">{r.proxies?.length || 0} proxies · order {r.order_id}</span>
                {r.proxies?.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => copy(r.proxies)} className="h-7 rounded-lg border-[#CBEEF5] bg-transparent text-ink hover:border-neon hover:text-neon" data-testid={`isp-assignment-copy-${r.order_id}`}><Copy className="w-3.5 h-3.5 mr-1" />Copy</Button>
                )}
              </div>
              {r.proxies?.length > 0 ? (
                <pre className="bg-[#061512] text-[#7DF5E4] text-xs font-mono rounded-xl p-4 max-h-56 overflow-auto whitespace-pre" data-testid={`isp-assignment-proxies-${r.order_id}`}>{r.proxies.join("\n")}</pre>
              ) : (
                <p className="text-zinc-400 text-sm">No proxies recorded for this order.</p>
              )}
            </td>
          </tr>
        )}
      </Fragment>
    ));
  };

  return (
    <div className="bg-white border border-[#E4EEEC] rounded-2xl overflow-hidden shadow-sm" data-testid="admin-isp-assignments">
      <div className="flex items-center justify-between p-4 border-b border-[#EFF6F5]">
        <h3 className="font-head text-lg font-semibold text-ink flex items-center gap-2"><ListChecks className="w-4 h-4 text-neon" aria-hidden="true" /> ISP Assignments <span className="text-zinc-400 text-sm font-normal">({rows.length})</span></h3>
        <button onClick={load} className="text-sm text-zinc-500 hover:text-neon flex items-center gap-1" data-testid="isp-assignments-refresh-button"><RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Refresh</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-isp-assignments-table">
          <thead><tr className="text-left text-zinc-400 font-mono text-xs uppercase border-b border-[#EFF6F5]">
            <th className="p-4">Customer</th><th className="p-4">IPs</th><th className="p-4">Status</th><th className="p-4">Subscription</th><th className="p-4">Proxies</th>
          </tr></thead>
          <tbody>{renderRows()}</tbody>
        </table>
      </div>
    </div>
  );
}
